/**
 * Map view of mountpoints (bases) and connected rovers.
 *
 * Data sources:
 *   - `usePositionsStream` — live WebSocket of decoded lat/lon (RTCM 1005/1006
 *     for bases, NMEA GGA for rovers).
 *   - `useMountpoints` + `useActiveConnections` — meta join for the popups
 *     (publisher/rover usernames, bytes, IPs).
 *
 * Bases without a decoded position do not render a marker — they're listed
 * in the header counter ("Z mountpoints sin posición") so the operator knows
 * they exist but haven't published a 1005 yet.
 *
 * The live stream is authoritative for *where* a base is, but the REST list
 * is authoritative for *whether* it exists: once `/api/mountpoints` has
 * loaded, positions whose name is not in that list are dropped (SOFT-2354 —
 * rtk-service used to keep deleted mountpoints in its registry until the
 * next restart; it now emits `mountpoint_removed`, and this filter is the
 * belt-and-braces guard for stale streams / old casters).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import MapGL, { Marker, Popup, NavigationControl, type MapRef } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { MapDistanceLines } from "@/components/MapDistanceLines";
import { MapMountpointPopup } from "@/components/MapMountpointPopup";
import { MapRoverPopup } from "@/components/MapRoverPopup";
import { useActiveConnections } from "@/hooks/useDashboard";
import { useMountpoints } from "@/hooks/useMountpoints";
import { usePositionsStream } from "@/hooks/usePositionsStream";
import { fixQualityMeta } from "@/lib/fixQuality";
import { formatAgo } from "@/lib/utils";
import type {
  ActiveConnection,
  Mountpoint,
  MountpointPosition,
  RoverPosition,
} from "@/types/api";

type Selection =
  | { kind: "mountpoint"; name: string }
  | { kind: "rover"; sessionId: string }
  | null;

/** Offline bases older than this get the lighter grey. */
export const OFFLINE_OLD_DAYS = 30;
const SHOW_OFFLINE_KEY = "caster-manager.map.showOffline";

function readShowOffline(): boolean {
  try {
    return localStorage.getItem(SHOW_OFFLINE_KEY) !== "0";
  } catch {
    return true;
  }
}

const DEFAULT_VIEW = {
  // España centrado — recentred on the first snapshot if we have data.
  latitude: 40.4,
  longitude: -3.7,
  zoom: 5.5,
};

export function MapPage() {
  const token = import.meta.env.VITE_MAPBOX_TOKEN ?? "";
  // Polled: the REST list decides which bases may be drawn, so it must not go
  // stale for long when a colleague creates/deletes a mountpoint elsewhere.
  const {
    data: mountpoints = [],
    isSuccess: mountpointsLoaded,
    refetch: refetchMountpoints,
  } = useMountpoints({ refetchInterval: 10_000 });
  const { data: connections = [] } = useActiveConnections();
  const stream = usePositionsStream({ enabled: true });

  const [selection, setSelection] = useState<Selection>(null);
  const [showOffline, setShowOffline] = useState<boolean>(readShowOffline);
  const mapRef = useRef<MapRef | null>(null);
  const didAutofit = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_OFFLINE_KEY, showOffline ? "1" : "0");
    } catch {
      /* private mode etc. — the toggle still works for this page load */
    }
  }, [showOffline]);

  // Join helpers
  const mountpointsByName = useMemo(() => {
    const m = new Map<string, Mountpoint>();
    mountpoints.forEach((mp) => m.set(mp.name, mp));
    return m;
  }, [mountpoints]);

  // Positions we actually render: the stream ∩ the REST list. Until the REST
  // list has loaded we show the stream as-is so the map doesn't flash empty.
  const visibleMountpoints = useMemo(() => {
    if (!mountpointsLoaded) return stream.mountpoints;
    const m = new Map<string, MountpointPosition>();
    stream.mountpoints.forEach((pos, name) => {
      if (mountpointsByName.has(name)) m.set(name, pos);
    });
    return m;
  }, [stream.mountpoints, mountpointsLoaded, mountpointsByName]);

  const connectionsByMp = useMemo(() => {
    const m = new Map<string, { publisher?: ActiveConnection; rovers: ActiveConnection[] }>();
    connections.forEach((c) => {
      const bucket = m.get(c.mountpoint) ?? { rovers: [] };
      if (c.role === "publisher") bucket.publisher = c;
      else bucket.rovers.push(c);
      m.set(c.mountpoint, bucket);
    });
    return m;
  }, [connections]);

  const connectionBySession = useMemo(() => {
    const m = new Map<string, ActiveConnection>();
    connections.forEach((c) => m.set(c.session_id, c));
    return m;
  }, [connections]);

  // Online = the caster says a publisher is connected (flag in the stream) or
  // we see a publisher session in /connections/active (older casters).
  const isOnline = (mp: MountpointPosition): boolean =>
    mp.online === true || !!connectionsByMp.get(mp.name)?.publisher;

  const { onlineMountpoints, offlineMountpoints } = useMemo(() => {
    const on = new Map<string, MountpointPosition>();
    const off = new Map<string, MountpointPosition>();
    visibleMountpoints.forEach((pos, name) => (isOnline(pos) ? on : off).set(name, pos));
    return { onlineMountpoints: on, offlineMountpoints: off };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMountpoints, connectionsByMp]);

  // What actually gets drawn: online always; offline only when the toggle is on.
  const renderedMountpoints = useMemo(() => {
    if (showOffline) return visibleMountpoints;
    return onlineMountpoints;
  }, [visibleMountpoints, onlineMountpoints, showOffline]);

  // A position for a name the REST list doesn't know yet — typically a
  // mountpoint created after this page loaded that just started publishing.
  // Refresh the list right away (debounced) so its marker shows up.
  useEffect(() => {
    if (!mountpointsLoaded) return;
    let unknown = false;
    stream.mountpoints.forEach((_, name) => {
      if (!mountpointsByName.has(name)) unknown = true;
    });
    if (!unknown) return;
    const t = window.setTimeout(() => void refetchMountpoints(), 300);
    return () => window.clearTimeout(t);
  }, [stream.mountpoints, mountpointsLoaded, mountpointsByName, refetchMountpoints]);

  // Auto-fit camera on the first batch of markers we receive.
  useEffect(() => {
    if (didAutofit.current) return;
    const points: [number, number][] = [];
    renderedMountpoints.forEach((mp) => points.push([mp.lon, mp.lat]));
    stream.rovers.forEach((r) => points.push([r.lon, r.lat]));
    if (points.length === 0) return;
    didAutofit.current = true;
    const map = mapRef.current;
    if (!map) return;
    if (points.length === 1) {
      map.flyTo({ center: points[0], zoom: 14 });
      return;
    }
    const lons = points.map(([lo]) => lo);
    const lats = points.map(([, la]) => la);
    map.fitBounds(
      [
        [Math.min(...lons), Math.min(...lats)],
        [Math.max(...lons), Math.max(...lats)],
      ],
      { padding: 80, duration: 800, maxZoom: 16 },
    );
  }, [renderedMountpoints, stream.rovers]);

  const missingPositionCount =
    mountpoints.filter((mp) => mp.is_enabled && !visibleMountpoints.has(mp.name)).length;

  if (!token) {
    return (
      <div className="view">
        <div className="view__title-block">
          <h1 className="view__title">Mapa</h1>
          <p className="view__subtitle">
            Falta configurar el token de Mapbox.
          </p>
        </div>
        <div className="card card--ticks" style={{ padding: 20 }}>
          <code className="mono">VITE_MAPBOX_TOKEN</code> no está definida.
          Pide el token y agrégalo a <code>.env.local</code>.
        </div>
      </div>
    );
  }

  // If the selected base vanishes (deleted while its popup is open) the popup
  // closes by itself because the lookup returns undefined.
  const selectedMountpoint =
    selection?.kind === "mountpoint" ? renderedMountpoints.get(selection.name) : undefined;
  const selectedMountpointMeta = selectedMountpoint
    ? mountpointsByName.get(selectedMountpoint.name)
    : undefined;
  const selectedRover =
    selection?.kind === "rover" ? stream.rovers.get(selection.sessionId) : undefined;

  return (
    <div className="view view--map">
      <div className="map-header">
        <div>
          <div className="view__eyebrow">
            <span className="tick" /> Live · Mapbox
          </div>
          <h1 className="view__title">Mapa</h1>
        </div>
        <div className="map-header__stats">
          <span>
            <strong data-testid="map-count-mountpoints">{renderedMountpoints.size}</strong>{" "}
            mountpoints en mapa
          </span>
          <span className="muted">
            · <span data-testid="map-count-online">{onlineMountpoints.size}</span> online
            {" · "}
            <span data-testid="map-count-offline">{offlineMountpoints.size}</span> offline
            {" · "}
            <span data-testid="map-count-missing">{missingPositionCount}</span> sin posición
          </span>
          <span>
            · <strong data-testid="map-count-rovers">{stream.rovers.size}</strong> rovers
          </span>
          <button
            type="button"
            className="map-toggle"
            aria-pressed={showOffline}
            data-testid="map-toggle-offline"
            title="Mostrar u ocultar las bases que no están publicando (última posición conocida)"
            onClick={() => setShowOffline((v) => !v)}
          >
            {showOffline ? "Ocultar offline" : "Mostrar offline"}
          </button>
          <span
            className={`map-header__status map-header__status--${stream.status}`}
            data-testid="map-stream-status"
            data-status={stream.status}
          >
            {stream.status === "open" ? "live" : stream.status}
          </span>
        </div>
      </div>

      <div className="map-canvas">
        <MapGL
          ref={mapRef}
          mapboxAccessToken={token}
          initialViewState={DEFAULT_VIEW}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          style={{ width: "100%", height: "100%" }}
        >
          <NavigationControl position="top-right" />

          {/* Baseline rover↔mountpoint (debajo de los markers para que clicks
              prioricen los puntos). */}
          <MapDistanceLines rovers={stream.rovers} mountpoints={onlineMountpoints} />

          {/* Mountpoint markers */}
          {Array.from(renderedMountpoints.values()).map((mp) => {
            const online = onlineMountpoints.has(mp.name);
            const ageDays = (Date.now() / 1000 - mp.updated_at) / 86_400;
            const offlineOld = !online && ageDays >= OFFLINE_OLD_DAYS;
            // Color: online → fix quality if the base sends GGA, else green.
            // Offline → grey (two shades: recent / old last publication).
            const color = online
              ? mp.fix_quality !== null
                ? fixQualityMeta(mp.fix_quality).hex
                : "#10b981"
              : offlineOld
              ? "#d1d5db"
              : "#9ca3af";
            const title = online
              ? mp.fix_quality !== null
                ? `${mp.name} (${fixQualityMeta(mp.fix_quality).label})`
                : `${mp.name} (online)`
              : `${mp.name} (offline · última publicación ${formatAgo(mp.updated_at)})`;
            const cls = online
              ? "map-marker map-marker--mp"
              : `map-marker map-marker--mp map-marker--offline${offlineOld ? " map-marker--offline-old" : ""}`;
            return (
              <Marker
                key={`mp-${mp.name}`}
                longitude={mp.lon}
                latitude={mp.lat}
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setSelection({ kind: "mountpoint", name: mp.name });
                }}
              >
                <div
                  className={cls}
                  style={{ background: color }}
                  title={title}
                  data-testid="map-marker-mp"
                  data-mountpoint={mp.name}
                  data-online={online ? "true" : "false"}
                  data-offline-old={offlineOld ? "true" : "false"}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="1.5" />
                    <path d="M8.5 8.5a5 5 0 0 0 0 7" />
                    <path d="M15.5 15.5a5 5 0 0 0 0-7" />
                  </svg>
                </div>
              </Marker>
            );
          })}

          {/* Rover markers */}
          {Array.from(stream.rovers.values()).map((rover: RoverPosition) => {
            const meta = fixQualityMeta(rover.quality);
            // Stale = no GGA for a while: grey, so a frozen position never
            // reads as a live FIX (SOFT-2380).
            const stale = rover.stale === true;
            return (
              <Marker
                key={`r-${rover.session_id}`}
                longitude={rover.lon}
                latitude={rover.lat}
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setSelection({ kind: "rover", sessionId: rover.session_id });
                }}
              >
                <div
                  className={`map-marker map-marker--rover${stale ? " map-marker--stale" : ""}`}
                  style={{ background: stale ? "#9ca3af" : meta.hex }}
                  title={
                    stale
                      ? `${rover.client_username} (sin datos)`
                      : `${rover.client_username} (${meta.label})`
                  }
                  data-testid="map-marker-rover"
                  data-session={rover.session_id}
                  data-username={rover.client_username}
                  data-stale={stale ? "true" : "false"}
                />
              </Marker>
            );
          })}

          {/* Mountpoint popup — needs both a live position and REST metadata
              (format, enabled flag); no stub is fabricated any more. */}
          {selectedMountpoint && selectedMountpointMeta && (
            <Popup
              longitude={selectedMountpoint.lon}
              latitude={selectedMountpoint.lat}
              anchor="top"
              closeOnClick={false}
              onClose={() => setSelection(null)}
              maxWidth="360px"
            >
              <MapMountpointPopup
                mountpoint={selectedMountpointMeta}
                position={selectedMountpoint}
                online={onlineMountpoints.has(selectedMountpoint.name)}
                publisher={connectionsByMp.get(selectedMountpoint.name)?.publisher}
                rovers={connectionsByMp.get(selectedMountpoint.name)?.rovers ?? []}
                roverPositions={stream.rovers}
              />
            </Popup>
          )}

          {/* Rover popup */}
          {selectedRover && (
            <Popup
              longitude={selectedRover.lon}
              latitude={selectedRover.lat}
              anchor="top"
              closeOnClick={false}
              onClose={() => setSelection(null)}
              maxWidth="320px"
            >
              <MapRoverPopup
                rover={selectedRover}
                connection={connectionBySession.get(selectedRover.session_id)}
              />
            </Popup>
          )}
        </MapGL>
      </div>
    </div>
  );
}
